import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import GeneralForm from '../../../components/generalForm';
import CustomSelect from '../../../components/customFields/customSelect';
import utils from '../../../utils/utils';
import {
  useDeliveryCreate,
  useDeliveryEdit,
  useDeliveryOrderBatchesWithoutDeliveries,
  useRoutePlans,
  useRoutePlanSetups,
} from '../apiHooks';
import RichText from '../../../components/customFields/richText';
import GooglePlacesField from '../../../components/customFields/googlePlacesField';
import ErrorMessage from '../../../components/errorMessage';

function DeliveryForm({ intl, onSuccess, routePlanId, existingDelivery, deliveryDayId, routeId, routes }) {
  const [delivery, setDelivery] = useState(
    existingDelivery
      ? {
          ...existingDelivery,
          routeId: routeId,
          address: {
            unit: existingDelivery.unit,
            street: existingDelivery.address,
            coordinates: existingDelivery.coordinates,
          },
        }
      : {
          routePlanId,
        }
  );

  // TODO load V2 route plans
  const {
    routePlans,
    isLoading: routePlansLoading,
    isError: routePlansIsError,
    error: routePlansError,
  } = useRoutePlans({
    deliveryDayId: deliveryDayId,
  });

  const {
    routePlanSetups,
    isLoading: routePlanSetupsLoading,
    isError: routePlanSetupsIsError,
    error: routePlanSetupsError,
  } = useRoutePlanSetups({
    deliveryDayId: deliveryDayId,
  });

  const {
    deliveryOrderBatches: deliveryOrderBatchesWithoutDeliveries,
    isLoading: loadingDeliveryOrderBatches,
    isError: isDeliveryOrderBatchesError,
    error: deliveryOrderBatchesError,
  } = useDeliveryOrderBatchesWithoutDeliveries({
    deliveryDayId: deliveryDayId,
  });

  const {
    createDelivery,
    isLoading: loading,
    isError: isError,
    error: createError,
  } = useDeliveryCreate((delivery) => onSuccess(delivery));

  const {
    editDelivery,
    isLoading: editLoading,
    isError: isEditError,
    error: editError,
  } = useDeliveryEdit((delivery) => onSuccess(delivery));

  const deliveryOrderBatchIdOptions = deliveryOrderBatchesWithoutDeliveries
    ? deliveryOrderBatchesWithoutDeliveries.map((dob) => {
        return {
          value: dob.id,
          label: intl.formatMessage(
            { id: `delivery.form.field.delivery.order.batch.value` },
            {
              deliveryOrderBatchId: dob.id,
              name: `${dob.packingId ? `#${dob.packingId}` : `D${dob.id}`} - ${
                dob.businessProfile ? dob.businessProfile.companyName : dob.user.screenName
              }`,
            }
          ),
        };
      })
    : [];

  const routePlanOptions = routePlans
    ? routePlans.map((routePlan) => {
        return {
          value: routePlan.id,
          label: intl.formatMessage(
            { id: `delivery.form.field.route.plan.value` },
            {
              routePlanId: routePlan.id,
              timeRange: utils.getFormattedTimeRange(routePlan.deliveryDayTimeRange),
            }
          ),
        };
      })
    : [];

  const routePlanSetupOptions = routePlanSetups
    ? routePlanSetups.map((routePlanSetup) => {
        return {
          value: routePlanSetup.id,
          label: `#${routePlanSetup.id}`,
        };
      })
    : [];

  const routeOptions = routes
    ? routes.map((route) => {
        return {
          value: route.id,
          label: intl.formatMessage(
            { id: `delivery.form.field.route.id.value` },
            {
              routeId: route.id,
              driver: route.driver ? route.driver.name : '-',
            }
          ),
        };
      })
    : [];

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      routePlanId: {
        type: 'string',
        title: intl.formatMessage({ id: 'delivery.form.field.route.plan.label' }),
        options: routePlanOptions,
      },
      routePlanSetupId: {
        type: 'string',
        title: intl.formatMessage({ id: 'delivery.form.field.route.plan.setup.label' }),
        options: routePlanSetupOptions,
      },
      routeId: {
        type: 'string',
        title: intl.formatMessage({ id: 'delivery.form.field.route.id.label' }),
        options: routeOptions,
        showClearOption: true,
      },
      deliveryOrderBatchId: {
        type: 'string',
        title: intl.formatMessage({ id: 'delivery.form.field.delivery.order.batch.label' }),
        options: deliveryOrderBatchIdOptions,
      },
      name: {
        type: 'string',
        title: intl.formatMessage({ id: 'delivery.form.field.name.label' }),
      },
      phoneNumber: {
        type: 'string',
        title: intl.formatMessage({ id: 'delivery.form.field.phone.number.label' }),
      },
      instructions: {
        type: 'string',
        title: intl.formatMessage({ id: 'delivery.form.field.instructions.label' }),
      },
      content: {
        type: 'string',
        title: intl.formatMessage({ id: 'delivery.form.field.content.label' }),
      },
      address: {
        type: 'object',
        required: [],
        properties: {
          unit: {
            type: 'string',
            default: '',
          },
          street: {
            type: 'string',
            default: '',
          },
          city: {
            type: 'string',
            default: '',
          },
          country: {
            type: 'string',
            default: '',
          },
          postCode: {
            type: 'string',
            default: '',
          },
          coordinates: {
            type: 'object',
            required: [],
            properties: {
              lat: {
                type: 'string',
              },
              lon: {
                type: 'string',
              },
            },
          },
        },
      },
      isIgnored: {
        type: 'boolean',
        title: intl.formatMessage({ id: 'delivery.form.field.edit.ignore.label' }),
      },
      editUserAddress: {
        type: 'boolean',
        title: intl.formatMessage({ id: 'delivery.form.field.edit.user.address.label' }),
      },
    },
  };

  const uiSchema = {
    address: {
      'ui:field': 'googlePlacesField',
    },
    content: {
      'ui:field': 'richText',
    },
    deliveryOrderBatchId: {
      'ui:field': 'customSelect',
    },
    routePlanId: {
      'ui:field': 'customSelect',
    },
    routePlanSetupId: {
      'ui:field': 'customSelect',
    },
    routeId: {
      'ui:field': 'customSelect',
    },
    instructions: {
      'ui:field': 'richText',
    },
  };

  const onFormSubmit = () => {
    try {
      if (existingDelivery) {
        editDelivery({ delivery });
      } else {
        createDelivery({ delivery });
      }
    } catch (e) {
      window.scrollTo(0, 0);
    }
  };

  const formFields = {
    googlePlacesField: GooglePlacesField,
    customSelect: CustomSelect,
    richText: RichText,
  };

  const buttonCopy = existingDelivery ? 'delivery.edit.form.submit' : 'delivery.create.form.submit';

  return (
    <div>
      <ErrorMessage
        error={isDeliveryOrderBatchesError ? utils.getErrorMessageFromResponse(deliveryOrderBatchesError) : null}
      />
      <GeneralForm
        onChange={(event) => setDelivery(event.formData)}
        onSubmit={onFormSubmit}
        formSchema={formSchema}
        formUiSchema={uiSchema}
        formFields={formFields}
        formData={delivery}
        buttonCopy={buttonCopy}
        errorMessage={
          isError || isEditError || routePlansIsError || routePlanSetupsIsError
            ? utils.getErrorMessageFromResponse(
                createError || editError || routePlansError || routePlanSetupsError,
                true
              )
            : null
        }
        loading={loading || loadingDeliveryOrderBatches || editLoading || routePlansLoading || routePlanSetupsLoading}
        hideImageUpload={true}
      />
    </div>
  );
}

export default injectIntl(DeliveryForm);
